import React from "react";
import type { LmsrQuote, LmsrSide } from "../utils/lmsrMath";
import { TRADING_TOKEN } from "../configs/config";
import {X} from 'lucide-react'

interface OutcomeOption {
  outcome_id: number;
  name: string;
}

interface BuyModalProps {
  isOpen: boolean;
  outcome: OutcomeOption | null;
  side: LmsrSide;
  outcomes: OutcomeOption[];
  marketVersion?: number;
  quantity: string;
  quote: LmsrQuote | null;
  currentRowPrices?: {
    yesPrice: number;
    noPrice: number;
  } | null;
  loading: boolean;
  isConfirmDisabled: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onQuantityChange: (value: string) => void;
}

const BuyModal: React.FC<BuyModalProps> = ({
  isOpen,
  outcome,
  side,
  outcomes,
  marketVersion,
  quantity,
  quote,
  currentRowPrices,
  loading,
  isConfirmDisabled,
  onClose,
  onConfirm,
  onQuantityChange,
}) => {
  if (!isOpen || !outcome) {
    return null;
  }

  const isBinaryMarket = marketVersion === 2;
  const isYesSide = side === "yes";
  const alternateOutcomeName =
    outcomes.find((item) => item.outcome_id !== outcome.outcome_id)?.name ?? "";
  const confirmLabel = isYesSide
    ? outcomes.find((item) => item.outcome_id === 0)?.name || "Yes"
    : outcomes.find((item) => item.outcome_id === 1)?.name || "No";
  const headingText = isBinaryMarket
    ? `Buy ${outcome.name}`
    : `Buy ${isYesSide ? "YES" : "NO"} - ${outcome.name}`;
  const descriptionText = isBinaryMarket
    ? `You are buying "${outcome.name}". If this outcome wins, you will receive 1 ${TRADING_TOKEN} per share.`
    : `You are buying "${isYesSide ? "YES" : "NO"}" shares for "${outcome.name}".${
        isYesSide
          ? ` If this outcome wins, you receive 1 ${TRADING_TOKEN} per share.`
          : ` If this outcome does NOT happen, you receive 1 ${TRADING_TOKEN} per share.`
      }`;
  const showAlternateOutcomeNote =
    !isBinaryMarket && !isYesSide && outcomes.length === 2;

  const MAX_SPEND_AMOUNT = 10000000;

  return (
    <div className="buy-modal-overlay" onClick={onClose}>
      <div className="buy-modal" onClick={(e) => e.stopPropagation()}>
        <div className="buy-modal-header">
          <h3>{headingText}</h3>
          <button className="buy-modal-close" onClick={onClose}>
            <X />
          </button>
        </div>
        <div className="buy-modal-content">
          <p className="buy-modal-description">{descriptionText}</p>
          {showAlternateOutcomeNote && (
            <p
              className="buy-modal-note"
              style={{
                fontSize: "0.85em",
                color: "#aaa",
                marginTop: "4px",
              }}
            >
              Note: Betting NO on "{outcome.name}" is equivalent to betting YES
              on "{alternateOutcomeName}".
            </p>
          )}
          <div className="buy-modal-form">
            {marketVersion === 3 && currentRowPrices && (
              <div className="buy-modal-quote">
                <div className="quote-row">
                  <span>YES Price:</span>
                  <strong>{currentRowPrices.yesPrice.toFixed(4)}</strong>
                </div>
                <div className="quote-row">
                  <span>NO Price:</span>
                  <strong>{currentRowPrices.noPrice.toFixed(4)}</strong>
                </div>
              </div>
            )}
            <label>
  {`Amount (${TRADING_TOKEN} to spend)`}               <input
                type="number"
                min="1"
                step="1"
                value={quantity}
                onChange={(e) => onQuantityChange(Math.min(parseInt(e.target.value), MAX_SPEND_AMOUNT).toString())}
                placeholder={`Enter amount in ${TRADING_TOKEN}`}
                autoFocus
              />
            </label>
            {quote && (
              <div className="buy-modal-quote">
                {quote.exceeds_liquidity_cap && (
                  <div
                    className="liquidity-warning"
                    style={{
                      background: "#3d2600",
                      border: "1px solid #f0a000",
                      borderRadius: "6px",
                      padding: "10px 12px",
                      marginBottom: "12px",
                      fontSize: "0.9em",
                      color: "#ffc107",
                    }}
                  >
                    <strong>Warning:</strong> Please reduce the spend. You are
                    not allowed to purchase more than the maximum allowed (
                    {quote.max_spend_amount !== undefined
                      ? `${quote.max_spend_amount.toFixed(2)} ${TRADING_TOKEN}`
                      : "N/A"}
                    ).
                  </div>
                )}
                <div className="quote-row">
                  <span>Estimated Shares:</span>
                  <strong>≈ {quote.estimated_shares?.toFixed(4)}</strong>
                </div>
                <div className="quote-row">
                  <span>Fee (1%):</span>
                  <strong>≈ {quote.fee?.toFixed(4)} ${TRADING_TOKEN}</strong>
                </div>
                <div className="quote-row">
                  <span>Avg Price/Share:</span>
                  <strong>≈ {quote.avg_price_per_share?.toFixed(4)} ${TRADING_TOKEN}</strong>
                </div>
                <div className="quote-row odds-change">
                  <span>New Odds (after buy):</span>
                  <strong>
                  {quote.new_odds_after_purchase.yes.toFixed(1)}%
                    {" / "}
                    {quote.new_odds_after_purchase.no.toFixed(1)}%
                  </strong>
                </div>
                <p className="buy-modal-payout">
                  If {isYesSide ? "this outcome wins" : "this outcome does NOT happen"}
                  , your estimated payout is up to{" "}
                  <strong className="buy-modal-payout-value">
                    ≈ {quote.estimated_shares?.toFixed(2)} ${TRADING_TOKEN}
                  </strong>
                  <span className="payout-disclaimer">
                    {" "}
                    * Final payout may be adjusted based on total pool's liquidity at the time of
                    resolution.
                  </span>
                </p>
              </div>
            )}
            {!quote && quantity && parseInt(quantity, 10) > 0 && (
              <p className="buy-modal-summary">
                Enter an amount to see estimated shares.
              </p>
            )}
          </div>
        </div>
        <div className="buy-modal-actions">
          <button className="buy-modal-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className={`buy-modal-confirm ${isYesSide ? "yes-btn" : "no-btn"}`}
            onClick={onConfirm}
            disabled={isConfirmDisabled}
          >
            {loading ? "Placing Order..." : `Buy ${confirmLabel}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BuyModal;
